import styled from '@emotion/styled';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import { navigationService } from 'src/config/rootService';
import greetingIcon from 'src/public/img/high-five.svg';
import { Button } from 'src/shared/components/Button';
import { Modal } from 'src/shared/components/Modal';
import { color, font, layout, mediaQueries } from 'src/shared/styles/variables';

const StyledModal = styled(Modal)({
    '&&': {
        padding: '0 2rem',

        '& .ant-modal-content': {
            maxWidth: layout.modalWidth.wide,
            margin: '0 auto',

            '& .ant-modal-body': {
                padding: '4rem 6rem',
                textAlign: 'center',
                [mediaQueries.sm]: {
                    padding: '4rem',
                },
            },
        },
    },
});

const GreetingModalContent = styled('div')({
    margin: '0 auto',
});

interface GreetingModalIconProps {
    iconUrl: string;
}

const GreetingModalIcon = styled('i')((props: GreetingModalIconProps) => ({
    display: 'block',
    backgroundImage: `url('${props.iconUrl}')`,
    height: '12rem',
    width: '12rem',
    margin: '0 auto',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const GreetingModalHeader = styled('h2')(
    {
        color: color.chathamsBlue,
        marginBottom: '2rem',
    },
    font.heavy(font.size.header),
);

const GreetingModalTextHolder = styled('div')(
    {
        color: color.tundora,
        '& p:not(:last-child)': {
            marginBottom: '1rem',
        },
        '& h5': {
            color: color.mamba,
            ...font.book(font.size.medium),
        },
    },
    font.book(font.size.large),
);

const ChildrenWrapper = styled('div')({
    marginTop: '1rem',
});

const Footer = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    [mediaQueries.md]: {
        flexDirection: 'column',
        '& > *:last-child': {
            margin: '1rem 0 0 0',
        },
    },
    '&': {
        button: {
            flex: '1',
        },
    },
});

interface Props extends WithTranslation {
    visible: boolean;
    content: string;
    isFirstWeek?: boolean;
    inDashboard?: boolean;
    onStartClick: () => void;
}

const GreetingModalContainer: React.FC<Props> = ({
    visible,
    onStartClick,
    isFirstWeek,
    inDashboard,
    content,
    children,
    t,
}) => (
    <StyledModal
        visible={visible}
        closable={false}
        width="100%"
        centered
        footer={
            <Footer>
                <Button
                    type="cancel"
                    onClick={() => navigationService.navigate('/dashboard')}
                    marginRight={8}
                >
                    {t('who5Modal.backToDashboardButton')}
                </Button>
                <Button type="accept" onClick={onStartClick} marginLeft={8}>
                    {t('who5Modal.startSurveyButton')}
                </Button>
            </Footer>
        }
    >
        <GreetingModalContent>
            {!isFirstWeek && <GreetingModalIcon iconUrl={greetingIcon} />}
            <GreetingModalHeader>
                {t(`who5Modal.${isFirstWeek || inDashboard ? 'title' : 'welcomeBack'}`)}
            </GreetingModalHeader>
            <GreetingModalTextHolder>
                <ReactMarkdown children={content} />
            </GreetingModalTextHolder>
            {children && <ChildrenWrapper>{children}</ChildrenWrapper>}
        </GreetingModalContent>
    </StyledModal>
);

export const AssessmentGreetingModal = withTranslation()(GreetingModalContainer);
